import { useState, useRef, useEffect } from 'react';
import { Bell, MessageCircle, AlertTriangle, Check, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';

export default function NotificationBell() {
  const { notifications, unreadCount, markAllRead, clearNotifications, permissionState, requestPermission } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClick = (notif) => {
    if (notif.sessionId) {
      navigate('/super-admin/chat');
    }
    setOpen(false);
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="sm"
        className="relative px-2"
        onClick={() => {
          setOpen(!open);
          if (!open && permissionState === 'default') requestPermission();
        }}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-14 sm:top-full sm:mt-2 w-auto sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded hover:bg-emerald-50 transition-colors"
                  data-testid="mark-all-read"
                >
                  <Check className="w-3 h-3 inline mr-0.5" />Mark read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearNotifications}
                  className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                  data-testid="clear-notifications"
                >
                  <Trash2 className="w-3 h-3 inline mr-0.5" />Clear
                </button>
              )}
            </div>
          </div>

          {/* Permission banner */}
          {permissionState === 'default' && (
            <button
              onClick={requestPermission}
              className="w-full px-4 py-2.5 text-xs text-amber-700 bg-amber-50 border-b border-amber-100 text-left hover:bg-amber-100 transition-colors"
              data-testid="enable-push-btn"
            >
              Enable browser notifications to get alerts even when this tab is in the background
            </button>
          )}

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {notifications.length === 0 ? (
              <div className="text-center text-gray-400 py-10">
                <Bell className="w-6 h-6 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50 ${
                    !n.read ? 'bg-blue-50/40' : ''
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex gap-2.5">
                    <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      n.type === 'escalation' ? 'bg-amber-100' : 'bg-blue-100'
                    }`}>
                      {n.type === 'escalation' ? (
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                      ) : (
                        <MessageCircle className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{n.title}</p>
                      <p className="text-xs text-gray-500 truncate">{n.body}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{formatTime(n.timestamp)}</p>
                    </div>
                    {!n.read && (
                      <div className="shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-2" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
